import { useEffect, useRef, useState, type ReactNode } from 'react';
import { themes } from '@circles/tokens';
import { Icon } from '../components/Icon';
import { Avatar, Button, NavBar, PhotoAvatar, cx } from '../components/ui';
import { setOnboarding, setState, useStore, type AuthStage } from '../state/store';
import { requiresAppLock } from '../data/mock';
import { circlesLogoUrl, resolveTheme } from '../state/theme';
import { useSizeClass } from '../shell/adaptive';

/** Matches an email to an organisation by registered domain (spec section 4, step 2). */
export function orgForEmail(email: string): string | null {
  const domain = email.split('@')[1]?.toLowerCase().trim();
  if (!domain) return null;
  const match = Object.values(themes).find((t) =>
    (t.emailDomains as readonly string[]).includes(domain),
  );
  return match?.id ?? null;
}

/** "anna.berg@…" -> "Anna Berg", to prefill the profile name. */
export function nameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  if (!/[._-]/.test(local)) return '';
  return local
    .split(/[._-]+/)
    .filter((p) => p.length > 1)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

export const TERMS_VERSION = '1.0 (2 November 2026)';
export const PRIVACY_VERSION = '1.0 (2 November 2026)';

export function SignIn() {
  const stage = useStore((s) => s.auth.stage);
  const { platform } = useSizeClass();
  // Desktop apps don't sign in on their own: the phone links them with a QR code (spec section 5).
  if (platform !== 'ios' && stage !== 'branding') {
    return (
      <div className="screen signin">
        <DesktopLink />
      </div>
    );
  }
  return (
    <div className="screen signin">
      {stage === 'welcome' && <Welcome />}
      {stage === 'email' && <EmailStep />}
      {stage === 'code' && <OtpStep channel="email" />}
      {stage === 'phone' && <PhoneStep />}
      {stage === 'sms' && <OtpStep channel="sms" />}
      {stage === 'terms' && <TermsStep />}
      {stage === 'profile' && <ProfileStep />}
      {stage === 'unlock' && <UnlockStep />}
      {stage === 'branding' && <Branding />}
    </div>
  );
}

const setStage = (stage: AuthStage) => setState((s) => ({ auth: { ...s.auth, stage } }));

// The five steps of spec section 5; the codes belong to their step.
const STEPS: { label: string; stages: AuthStage[] }[] = [
  { label: 'Email', stages: ['email', 'code'] },
  { label: 'Phone', stages: ['phone', 'sms'] },
  { label: 'Terms', stages: ['terms'] },
  { label: 'Profile', stages: ['profile'] },
  { label: 'Unlock', stages: ['unlock'] },
];

function StepHeader({ stage, back }: { stage: AuthStage; back: AuthStage }) {
  const index = STEPS.findIndex((s) => s.stages.includes(stage));
  return (
    <NavBar
      onBack={() => setStage(back)}
      title={
        <span className="step-progress" aria-label={`Step ${index + 1} of ${STEPS.length}`}>
          {STEPS.map((s, i) => (
            <span
              key={s.label}
              className={cx('step-dot', i < index && 'is-done', i === index && 'is-current')}
            />
          ))}
        </span>
      }
      subtitle={
        <span className="step-caption">
          Step {index + 1} of {STEPS.length} · {STEPS[index]?.label}
        </span>
      }
      center
    />
  );
}

function StepLayout({
  stage,
  back,
  title,
  text,
  children,
  footer,
}: {
  stage: AuthStage;
  back: AuthStage;
  title: string;
  text?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <>
      <StepHeader stage={stage} back={back} />
      <div className="signin-body">
        <h1 className="signin-title">{title}</h1>
        {text && <p className="signin-text">{text}</p>}
        {children}
      </div>
      {footer && <div className="signin-footer">{footer}</div>}
    </>
  );
}

function Welcome() {
  return (
    <div className="welcome">
      <div className="welcome-mark">
        <img src={circlesLogoUrl} alt="" width={132} height={132} />
      </div>
      <h1 className="welcome-title">Circles</h1>
      <p className="welcome-tag">
        People who matter. Private messaging and news for your organisation, hosted in Germany.
      </p>
      <div className="welcome-actions">
        <Button block onClick={() => setStage('email')}>
          Get started
        </Button>
        <p className="fine-print">
          Setup takes about two minutes: email, phone, terms and your profile.
        </p>
      </div>
    </div>
  );
}

function EmailStep() {
  const email = useStore((s) => s.auth.email);
  const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const setEmail = (v: string) => setState((s) => ({ auth: { ...s.auth, email: v } }));
  return (
    <StepLayout
      stage="email"
      back="welcome"
      title="What’s your email?"
      text="Use your work email. If your organisation uses Circles, you’ll join it automatically."
      footer={
        <Button block disabled={!valid} onClick={() => setStage('code')}>
          Send code
        </Button>
      }
    >
      <label className="field">
        <span className="field-label">Email</span>
        <input
          className="input input-large"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          placeholder="name@company.com"
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && valid && setStage('code')}
          autoFocus
        />
      </label>
      <div className="demo-hint">
        <Icon name="help" size={15} />
        <span>
          Try{' '}
          <button
            type="button"
            className="text-link inline"
            onClick={() => setEmail('anna.berg@nordwind-logistik.de')}
          >
            anna.berg@nordwind-logistik.de
          </button>
          ,{' '}
          <button
            type="button"
            className="text-link inline"
            onClick={() => setEmail('m.falk@praxis-am-park.de')}
          >
            m.falk@praxis-am-park.de
          </button>{' '}
          or any other address for the neutral design.
        </span>
      </div>
    </StepLayout>
  );
}

/** Email code (step 2) and SMS code (step 3): 6 digits, 10 minutes, at most 5 attempts. */
function OtpStep({ channel }: { channel: 'email' | 'sms' }) {
  const email = useStore((s) => s.auth.email);
  const phone = useStore((s) => s.onboarding.phone);
  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(42);
  const input = useRef<HTMLInputElement>(null);
  const sms = channel === 'sms';
  const suggestion = sms ? '731204' : '482913';

  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (code.length !== 6) return undefined;
    const t = setTimeout(() => {
      if (!sms) setState({ orgId: null });
      setStage(sms ? 'terms' : 'phone');
    }, 350);
    return () => clearTimeout(t);
  }, [code, sms]);

  return (
    <StepLayout
      stage={sms ? 'sms' : 'code'}
      back={sms ? 'phone' : 'email'}
      title={sms ? 'Enter the SMS code' : 'Enter the code'}
      text={
        <>
          We sent a 6-digit code to{' '}
          <strong>{sms ? `+49 ${phone || '…'}` : email || 'your email'}</strong>. It’s valid for 10
          minutes.
        </>
      }
    >
      <div className="code-boxes" onClick={() => input.current?.focus()}>
        {Array.from({ length: 6 }, (_, i) => (
          <span
            key={i}
            className={cx('code-box', i === code.length && 'is-active', code[i] && 'is-filled')}
          >
            {code[i] ?? ''}
          </span>
        ))}
        <input
          ref={input}
          className="code-input"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          aria-label="6-digit code"
          autoFocus
        />
      </div>
      {/* iOS offers the code above the keyboard; Android reads the SMS by itself (spec section 5). */}
      <button type="button" className="code-autofill" onClick={() => setCode(suggestion)}>
        <Icon name={sms ? 'chats' : 'mail'} size={16} /> From {sms ? 'Messages' : 'Mail'}:{' '}
        <strong>
          {suggestion.slice(0, 3)} {suggestion.slice(3)}
        </strong>
      </button>
      <p className="signin-text small">
        {seconds > 0 ? (
          `Resend code in 0:${String(seconds).padStart(2, '0')}`
        ) : (
          <button type="button" className="text-link inline">
            Resend code
          </button>
        )}
        {sms && (
          <>
            {' · '}
            <button type="button" className="text-link inline" onClick={() => setStage('phone')}>
              Change number
            </button>
          </>
        )}
      </p>
      <p className="demo-hint">
        <Icon name="help" size={15} /> Prototype: any 6 digits work.
      </p>
    </StepLayout>
  );
}

function PhoneStep() {
  const phone = useStore((s) => s.onboarding.phone);
  const digits = phone.replace(/\D/g, '');
  const valid = digits.length >= 9 && digits.length <= 12;
  return (
    <StepLayout
      stage="phone"
      back="code"
      title="Your mobile number"
      text="We send a code to confirm it. You need this number to recover your account. Your colleagues and your organisation’s admin can’t see it."
      footer={
        <Button block disabled={!valid} onClick={() => setStage('sms')}>
          Send SMS code
        </Button>
      }
    >
      <div className="field">
        <span className="field-label">Mobile number</span>
        <div className="phone-row">
          <button
            type="button"
            className="input input-large country"
            aria-label="Country code: Germany +49"
          >
            <span aria-hidden>🇩🇪</span> +49
          </button>
          <input
            className="input input-large"
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            placeholder="151 23456789"
            value={phone}
            onChange={(e) => setOnboarding({ phone: e.target.value.replace(/[^\d ]/g, '') })}
            onKeyDown={(e) => e.key === 'Enter' && valid && setStage('sms')}
            aria-label="Mobile number"
            autoFocus
          />
        </div>
      </div>
      <button
        type="button"
        className="code-autofill"
        onClick={() => setOnboarding({ phone: '151 23456789' })}
      >
        <Icon name="phone" size={16} /> Use this phone’s number
      </button>
    </StepLayout>
  );
}

function TermsStep() {
  const { terms, privacy } = useStore((s) => s.onboarding);
  return (
    <StepLayout
      stage="terms"
      back="sms"
      title="Terms and privacy"
      text="Please read and accept both. We save each consent with the version you accepted."
      footer={
        <Button block disabled={!terms || !privacy} onClick={() => setStage('profile')}>
          Continue
        </Button>
      }
    >
      <div className="consent-list">
        <Consent
          checked={terms}
          onChange={(v) => setOnboarding({ terms: v })}
          title={
            <>
              I accept the <a>terms of use</a>
            </>
          }
          version={TERMS_VERSION}
        />
        <Consent
          checked={privacy}
          onChange={(v) => setOnboarding({ privacy: v })}
          title={
            <>
              I have read the <a>privacy policy</a>
            </>
          }
          version={PRIVACY_VERSION}
        />
      </div>
      <ul className="facts">
        <li>
          <Icon name="lock" size={16} /> Your chats and calls are end-to-end encrypted. Nobody else
          can read them, not even Circles or your organisation.
        </li>
        <li>
          <Icon name="shield" size={16} /> Your data is stored in Germany.
        </li>
      </ul>
    </StepLayout>
  );
}

function Consent({
  checked,
  onChange,
  title,
  version,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: ReactNode;
  version: string;
}) {
  return (
    <label className={cx('consent', checked && 'is-checked')}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="checkbox" aria-hidden>
        {checked && <Icon name="tickSent" size={16} strokeWidth={3} />}
      </span>
      <span className="consent-text">
        <span className="consent-title">{title}</span>
        <span className="consent-version">Version {version}</span>
      </span>
    </label>
  );
}

function ProfileStep() {
  const email = useStore((s) => s.auth.email);
  const { name, title, photo } = useStore((s) => s.onboarding);
  const hasOrg = orgForEmail(email) !== null;

  // Prefill the name once from the email address; the user can change it.
  useEffect(() => {
    if (!name) setOnboarding({ name: nameFromEmail(email) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepLayout
      stage="profile"
      back="terms"
      title="Your profile"
      text={
        hasOrg
          ? 'Colleagues and people you chat with see your name, photo and job title.'
          : 'People you chat with see your name and photo.'
      }
      footer={
        <Button block disabled={!name.trim()} onClick={() => setStage('unlock')}>
          Continue
        </Button>
      }
    >
      <div className="photo-picker">
        <button
          type="button"
          className="photo-button"
          onClick={() => setOnboarding({ photo: !photo })}
          aria-label={photo ? 'Remove photo' : 'Add photo'}
        >
          {photo ? (
            <PhotoAvatar hue="orange" size={104} />
          ) : (
            <Avatar name={name || '?'} hue="orange" size={104} />
          )}
          <span className="photo-badge">
            <Icon name={photo ? 'edit' : 'camera'} size={16} />
          </span>
        </button>
        <span className="photo-hint">
          {photo ? 'Tap to change or remove' : 'Add a photo (optional)'}
        </span>
      </div>
      <label className="field">
        <span className="field-label">Name</span>
        <input
          className="input input-large"
          value={name}
          autoComplete="name"
          placeholder="First and last name"
          onChange={(e) => setOnboarding({ name: e.target.value })}
        />
      </label>
      <label className="field">
        <span className="field-label">Job title (optional)</span>
        <input
          className="input input-large"
          value={title}
          autoComplete="organization-title"
          placeholder={hasOrg ? 'e.g. Head of Dispatch' : 'Optional'}
          onChange={(e) => setOnboarding({ title: e.target.value })}
        />
      </label>
    </StepLayout>
  );
}

function UnlockStep() {
  const email = useStore((s) => s.auth.email);
  const org = orgForEmail(email);
  const required = requiresAppLock(org);
  const orgName = org ? resolveTheme(org, null, 'light').orgName : null;
  const finish = (unlock: 'biometric' | 'passcode' | null) => {
    setOnboarding({ unlock });
    setStage('branding');
  };
  return (
    <StepLayout
      stage="unlock"
      back="profile"
      title="Lock Circles"
      text="Circles asks for Face ID or your device passcode when you open it, so nobody else can read your chats on this phone."
      footer={
        <div className="footer-stack">
          <Button block icon="lock" onClick={() => finish('biometric')}>
            Use Face ID
          </Button>
          <Button
            block
            variant={required ? 'quiet' : 'secondary'}
            onClick={() => finish('passcode')}
          >
            Use device passcode instead
          </Button>
          {!required && (
            <Button block variant="quiet" onClick={() => finish(null)}>
              Set up later
            </Button>
          )}
        </div>
      }
    >
      <div className="unlock-art" aria-hidden>
        <svg viewBox="0 0 120 120" width="120" height="120">
          <path d="M30 12H20a8 8 0 0 0-8 8v10M90 12h10a8 8 0 0 1 8 8v10M30 108H20a8 8 0 0 1-8-8V90M90 108h10a8 8 0 0 0 8-8V90" />
          <path d="M42 44v8M78 44v8M60 46v20h-6M46 82c8 7 20 7 28 0" />
        </svg>
      </div>
      {required ? (
        <p className="demo-hint">
          <Icon name="shield" size={15} /> {orgName} requires an app lock for everyone.
        </p>
      ) : (
        <p className="signin-text small center">You can turn it on later in You → Privacy.</p>
      )}
    </StepLayout>
  );
}

/** Desktop (macOS, Windows): linked from the phone by QR code, verified by the phone. */
function DesktopLink() {
  const { platform } = useSizeClass();
  return (
    <div className="desktop-link">
      <div className="desktop-link-text">
        <img src={circlesLogoUrl} alt="" width={56} height={56} />
        <h1>Use Circles on this {platform === 'macos' ? 'Mac' : 'PC'}</h1>
        <ol>
          <li>Open Circles on your phone.</li>
          <li>
            Go to <strong>You → Linked devices</strong> and tap <strong>Link a desktop</strong>.
          </li>
          <li>Point your phone at this code.</li>
        </ol>
        <p className="signin-text small">
          Your phone checks this computer and shares your chat keys with it. Up to 4 computers can
          be linked.
        </p>
        <p className="demo-hint">
          <Icon name="help" size={15} /> Don’t have Circles on your phone yet? Set it up there
          first. On a desktop there is no separate sign-in.
        </p>
      </div>
      <div className="desktop-link-qr">
        <QrPlaceholder />
        <Button
          small
          variant="secondary"
          onClick={() =>
            setState((s) => ({
              auth: { stage: 'branding', email: s.auth.email || 'anna.berg@nordwind-logistik.de' },
            }))
          }
        >
          Prototype: simulate the scan
        </Button>
      </div>
    </div>
  );
}

/** A decorative QR-like pattern; not a real code. */
function QrPlaceholder() {
  const cells: [number, number][] = [];
  let seed = 11;
  for (let y = 0; y < 25; y++) {
    for (let x = 0; x < 25; x++) {
      seed = (seed * 9301 + 49297) % 233280;
      const finder = (x < 8 && y < 8) || (x > 16 && y < 8) || (x < 8 && y > 16);
      if (!finder && seed / 233280 > 0.52) cells.push([x, y]);
    }
  }
  const finder = (x: number, y: number) => (
    <g key={`${x}-${y}`}>
      <rect
        x={x + 0.5}
        y={y + 0.5}
        width="6"
        height="6"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
      />
      <rect x={x + 2} y={y + 2} width="3" height="3" rx=".6" />
    </g>
  );
  return (
    <svg
      className="qr"
      viewBox="0 0 25 25"
      width="220"
      height="220"
      role="img"
      aria-label="QR code for linking"
    >
      {cells.map(([x, y]) => (
        <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" />
      ))}
      {finder(0, 0)}
      {finder(18, 0)}
      {finder(0, 18)}
      <image href={circlesLogoUrl} x="10" y="10" width="5" height="5" />
    </svg>
  );
}

/** After sign-in: the organisation's branding downloads and replaces the neutral design. */
function Branding() {
  const email = useStore((s) => s.auth.email);
  const mode = useStore((s) => s.mode);
  const org = orgForEmail(email);
  const target = resolveTheme(org, null, mode);
  const [phase, setPhase] = useState<'matching' | 'applying' | 'done'>('matching');

  useEffect(() => {
    const t1 = setTimeout(() => {
      setPhase('applying');
      setState({ orgId: org });
    }, 1400);
    const t2 = setTimeout(() => setPhase('done'), 2600);
    const t3 = setTimeout(
      () =>
        setState((s) => ({
          auth: { ...s.auth, stage: 'in' },
          tab: 'chats',
          stacks: { chats: [], feed: [], calls: [], you: [] },
        })),
      3800,
    );
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, [org]);

  return (
    <div className={cx('branding', `is-${phase}`)}>
      <div className="branding-stage">
        <img className="branding-circles" src={circlesLogoUrl} alt="" width={96} height={96} />
        {target.orgName && target.logo && (
          <img className="branding-org" src={target.logo} alt="" width={96} height={96} />
        )}
        <svg className="branding-ring" viewBox="0 0 120 120" aria-hidden>
          <circle cx="60" cy="60" r="56" />
        </svg>
      </div>
      <div className="branding-text">
        {phase === 'matching' && (
          <>
            <h1>Signing you in…</h1>
            <p>Looking up your organisation</p>
          </>
        )}
        {phase !== 'matching' &&
          (target.orgName ? (
            <>
              <h1>Welcome to {target.orgName}</h1>
              <p>Loading your organisation’s design and channels</p>
            </>
          ) : (
            <>
              <h1>Welcome to Circles</h1>
              <p>You can chat with anyone who invites you</p>
            </>
          ))}
      </div>
      <div className="branding-steps">
        <span className={cx('step', 'is-done')}>
          <Icon name="tickSent" size={14} strokeWidth={2.4} /> Email verified
        </span>
        <span className={cx('step', phase !== 'matching' && 'is-done')}>
          <Icon name={phase !== 'matching' ? 'tickSent' : 'more'} size={14} strokeWidth={2.4} />{' '}
          {target.orgName ? 'Organisation found' : 'No organisation for this email'}
        </span>
        <span className={cx('step', phase === 'done' && 'is-done')}>
          <Icon name={phase === 'done' ? 'tickSent' : 'more'} size={14} strokeWidth={2.4} />{' '}
          {target.orgName ? 'Design applied' : 'Neutral design'}
        </span>
      </div>
    </div>
  );
}
